import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { DealData } from "../../../Models/ambassador";
import { InfoRow } from "../Common/AmbassadorUi";
import { formatQuarter, formatVolume } from "../Common/format";
import { useAmbassadorLabels } from "../Common/useAmbassadorLabels";

interface Props {
  data: DealData;
  /** Shows an edit button per section that opens that form step. */
  onEdit?: (step: number) => void;
}

/** The deal record, read-only, in the sections of the form steps. */
const DealDataSummary: React.FC<Props> = ({ data, onEdit }) => {
  const { t, i18n } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();

  const section = (
    step: number,
    title: string,
    rows: [string, React.ReactNode][],
  ) => (
    <Box sx={{ "& + &": { mt: 3 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          sx={{ fontSize: 14, fontWeight: 800, color: color.gray900 }}
        >
          {title}
        </Typography>
        {onEdit && (
          <Button
            size="small"
            color="secondary"
            startIcon={<EditOutlined />}
            onClick={() => onEdit(step)}
          >
            {t("common.edit")}
          </Button>
        )}
      </Box>
      {rows.map(([field, value]) => (
        <InfoRow key={field} label={t(`fields.${field}`)} value={value} />
      ))}
    </Box>
  );

  const option = <T,>(value: T | null, label: (value: T) => string) =>
    value === null ? "" : label(value);

  const multiline = (value: string) =>
    value && (
      <Box component="span" sx={{ whiteSpace: "pre-wrap", fontWeight: 500 }}>
        {value}
      </Box>
    );

  return (
    <>
      {section(0, t("wizard.steps.customer"), [
        ["company_name", data.company_name],
        [
          "customer_relationship",
          option(data.customer_relationship, labels.customerRelationship),
        ],
        ["company_website", data.company_website],
        [
          "company_country",
          data.company_country && labels.country(data.company_country),
        ],
        ["contact_name", data.contact_name],
        ["contact_role", data.contact_role],
        ["contact_email", data.contact_email],
        ["contact_agreed", option(data.contact_agreed, labels.contactAgreed)],
      ])}
      {section(1, t("wizard.steps.project"), [
        ["title", data.title],
        [
          "opportunity_type",
          option(data.opportunity_type, labels.opportunityType),
        ],
        ["description", multiline(data.description)],
        [
          "service_areas",
          data.service_areas.map(labels.serviceArea).join(", "),
        ],
        [
          "expected_start",
          data.expected_start && formatQuarter(data.expected_start),
        ],
        [
          "expected_volume",
          data.expected_volume !== null &&
            t("wizard.volumeEstimate", {
              volume: formatVolume(
                data.expected_volume,
                data.currency,
                i18n.language,
              ),
            }),
        ],
        ["deal_readiness", option(data.deal_readiness, labels.dealReadiness)],
      ])}
      {section(2, t("wizard.steps.classification"), [
        ["contribution", option(data.contribution, labels.contribution)],
        ["next_step", multiline(data.next_step)],
        ["additional_info", multiline(data.additional_info)],
      ])}
    </>
  );
};

export default DealDataSummary;
